import type { ReactNode } from 'react';
import { useMe } from '@features/user-profile/hooks/useMe';
import type { MeSubscription } from '@features/user-profile/hooks/useMe';
import { SubscribePage } from './SubscribePage';
import { useSubscriptionChangedInvalidation } from './useSubscriptionChangedInvalidation';
import { featurePlans } from '@config/env';
import { BrandLoader } from '@shared/ui';

interface SubscriptionGuardProps {
  children: ReactNode;
}

/**
 * Entitlement is server-computed and override-aware (`MeSubscription.isEntitled`) — the BE has
 * already folded status, period end, cancellation and any manual override into that one field.
 * This helper exists only so the null-check + field read is unit-testable in isolation; it must
 * never grow logic that re-derives entitlement from `status`/`currentPeriodEnd` client-side.
 */
export function isEntitled(sub: MeSubscription | null | undefined): boolean {
  if (sub == null) return true; // billing feature dark, or /me predates the field — BE gate is authority
  return sub.isEntitled;
}

/**
 * Nests inside `ApprovalGuard` (approval must be resolved before the paywall is even relevant) and
 * wraps `AppShell`. Mirrors `ApprovalGuard`'s pending splash verbatim; unlike `ApprovalGuard`, an
 * errored `/me` here just fails open (renders children) rather than showing its own recovery
 * screen — by the time this guard mounts, `ApprovalGuard` has already handled the persistent
 * network/timeout case, so this branch is a defensive backstop, and the BE gate is the real
 * enforcement either way.
 *
 * Also wires `useSubscriptionChangedInvalidation()` — called unconditionally, BEFORE any of the
 * branches below return, so the live-unlock listener is active whether this guard is currently
 * blocking (rendering `SubscribePage`) or not. This is the fix for the "live unlock never reaches
 * the paywall" bug: `useRealtimeInvalidation` (which used to own this subscription) only runs inside
 * `AppShell`, which never mounts while this guard blocks — so a checkout completing elsewhere while
 * the user stared at the paywall never invalidated `['me']`. `SubscriptionGuard` itself, by contrast,
 * always mounts for an approved user, blocked or not.
 */
export function SubscriptionGuard({ children }: SubscriptionGuardProps) {
  const me = useMe();
  useSubscriptionChangedInvalidation();

  if (me.isPending) {
    return (
      <div className="flex h-screen w-full flex-col items-center justify-center gap-4 bg-canvas text-text">
        <BrandLoader decorative />
        <p className="text-neutral-500">Signing you in…</p>
      </div>
    );
  }

  if (me.isError) {
    return <>{children}</>;
  }

  // With plans feature-flagged off there is no way to BUY an entitlement, so substituting the pricing
  // page would lock the user out of a product they cannot unlock — a paywall with no door (2026-08-17).
  // Fails open, exactly as a null subscription (billing dark) already does.
  if (featurePlans && !isEntitled(me.data?.subscription)) {
    // `standalone`: this guard renders in AppShell's own place, so there is no shell to frame the
    // page in — it must be the full-bleed takeover (`fixed inset-0 z-30`).
    return <SubscribePage standalone />;
  }

  return <>{children}</>;
}
